/** Bankruptcy route control (A2) — Customer File header, legal mode. Start the route / move it along; pause, close and
 *  "not appropriate" need a reason. Allowed moves come from the server (state machine in services/legal_route.py). */
import { useEffect, useState } from "react";
import { Route, ChevronDown, Pause, Play, Ban, XCircle, ArrowRight, History, FileCheck2, ShieldCheck, ClipboardList, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { getCaseGates, getCaseRoute, moveCaseRoute } from "@/lib/api";
import GateChecklistDialog from "@/pages/legal/GateChecklistDialog";
import ServiceRecordDialog from "@/pages/legal/ServiceRecordDialog";
import ReviewOutcomeDialog from "@/pages/legal/ReviewOutcomeDialog";
import PetitionHubDialog from "@/pages/legal/petition/PetitionHubDialog";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const REVIEW_STATUSES = ["sd_served", "post_demand_review"];
const PETITION_STATUSES = ["prepare_petition", "awaiting_hearing", "order_made", "referred_menzies"];

const TONE = {
  referred_unassigned: "bg-slate-100 text-slate-700 border-slate-300",
  eligibility_check: "bg-sky-50 text-sky-800 border-sky-300",
  pre_action: "bg-amber-50 text-amber-900 border-amber-300",
  ready_for_sd: "bg-orange-50 text-orange-900 border-orange-300",
  sd_served: "bg-orange-100 text-orange-900 border-orange-400",
  post_demand_review: "bg-indigo-50 text-indigo-800 border-indigo-300",
  prepare_petition: "bg-violet-50 text-violet-800 border-violet-300",
  awaiting_hearing: "bg-rose-50 text-rose-800 border-rose-300",
  order_made: "bg-slate-800 text-white border-slate-800",
  referred_menzies: "bg-emerald-50 text-emerald-800 border-emerald-300",
  route_paused: "bg-yellow-50 text-yellow-900 border-yellow-300",
  not_appropriate: "bg-slate-50 text-slate-600 border-slate-300",
  route_closed: "bg-slate-50 text-slate-500 border-slate-200",
};
const ICON = { route_paused: Pause, route_closed: XCircle, not_appropriate: Ban };

export default function RouteStatusControl({ caseRow, onChanged }) {
  const [route, setRoute] = useState(null);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [gateFor, setGateFor] = useState(null);      // {gate, to, label}
  const [service, setService] = useState(null);     // service methods list when the dialog is open
  const [review, setReview] = useState(false);      // Stage 4 "Record review outcome" dialog
  const [petition, setPetition] = useState(false);  // C1 petition hub (Stages 5 → 6)

  const load = async () => {
    try { setRoute(await getCaseRoute(caseRow.case_id)); } catch { setRoute(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseRow.case_id, caseRow.bankruptcy_route?.status]);

  const applied = async (res, to) => {
    const n = (res.created_events || []).length;
    toast.success(`Bankruptcy route → ${res.route?.label || to}${n ? ` · ${n} action${n === 1 ? "" : "s"} diarised` : ""}${res.ladder_stage ? ` · stage ${res.ladder_stage}` : ""}`);
    setPending(null); setReason(""); setGateFor(null);
    setRoute(res.route);
    await onChanged?.();
  };
  const doMove = async (to, why) => {
    setBusy(true);
    try {
      await applied(await moveCaseRoute(caseRow.case_id, to, why), to);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gate) {
        const m = (route?.allowed || []).find((x) => x.to === to);
        setGateFor({ gate: d.gate.gate, to, label: m?.label || to });
        toast.error(errorDetailMessage(e, ""));
      } else {
        toast.error("Route move failed: " + errorDetailMessage(e, e.message));
      }
    } finally { setBusy(false); }
  };
  const pick = (m) => (["awaiting_hearing", "order_made", "referred_menzies"].includes(m.to) ? setPetition(true) : m.reason_required ? setPending(m) : m.gate ? setGateFor({ gate: m.gate, to: m.to, label: m.label }) : doMove(m.to));
  const openService = async () => {
    try { const g = await getCaseGates(caseRow.case_id); setService(g.service_methods || []); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); }
  };

  if (!route) return null;
  const allowed = route.allowed || [];
  if (!route.on_route) {
    const start = allowed.find((m) => m.start);
    return (
      <Button size="sm" variant="outline" className="h-8 text-xs border-red-200 text-red-800 bg-red-50/60 hover:bg-red-100" onClick={() => start && doMove(start.to)} disabled={busy || !start} data-testid="route-start-btn" title="Put this file on the E&W personal bankruptcy route — eligibility check first">
        <Route className="w-3.5 h-3.5 mr-1" /> Start bankruptcy route
      </Button>
    );
  }
  const Icon = ICON[route.status] || Route;
  const forward = allowed.filter((m) => !m.reason_required);
  const side = allowed.filter((m) => m.reason_required);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={`h-8 inline-flex items-center gap-1.5 px-2.5 rounded-md border text-xs font-medium ${TONE[route.status] || TONE.route_closed}`} disabled={busy} data-testid="route-chip" title={`Bankruptcy route — ${route.label}${route.stage_no ? ` (stage ${route.stage_no})` : ""}${route.since ? ` since ${new Date(route.since).toLocaleDateString("en-GB")}` : ""}`}>
            <Icon className="w-3.5 h-3.5" /> {route.label}
            {!route.terminal && <ChevronDown className="w-3 h-3 opacity-70" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="text-xs w-72" data-testid="route-menu">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">Bankruptcy route{route.stage_no != null ? ` · stage ${route.stage_no}` : ""}</DropdownMenuLabel>
          {route.reason && <div className="px-2 pb-1 text-[11px] text-slate-600 italic truncate" title={route.reason}>“{route.reason}”</div>}
          {forward.length > 0 && <DropdownMenuSeparator />}
          {forward.map((m) => (
            <DropdownMenuItem key={m.to} onClick={() => pick(m)} data-testid={`route-move-${m.to}`}>
              {m.resume ? <Play className="w-3.5 h-3.5 mr-2 text-emerald-600" /> : m.gate ? <ShieldCheck className="w-3.5 h-3.5 mr-2 text-indigo-600" /> : <ArrowRight className="w-3.5 h-3.5 mr-2 text-slate-500" />}
              <span className="flex-1">{m.resume ? `Resume — ${m.label}` : m.label}</span>
              {m.gate && <span className="ml-2 text-[9px] font-mono px-1 rounded border bg-indigo-50 text-indigo-700 border-indigo-200" title={`Gate ${m.gate} — the checklist opens before this move`}>{m.gate}</span>}
            </DropdownMenuItem>
          ))}
          {REVIEW_STATUSES.includes(route.status) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setReview(true)} className="text-indigo-800 font-medium" data-testid="route-record-review">
                <ClipboardList className="w-3.5 h-3.5 mr-2" /> Record review outcome…
              </DropdownMenuItem>
            </>
          )}
          {PETITION_STATUSES.includes(route.status) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPetition(true)} className="text-violet-800 font-medium" data-testid="route-petition-hub">
                <ScrollText className="w-3.5 h-3.5 mr-2" /> Bankruptcy petition…
              </DropdownMenuItem>
            </>
          )}
          {["ready_for_sd", "sd_served"].includes(route.status) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openService} className="text-orange-800" data-testid="route-record-service">
                <FileCheck2 className="w-3.5 h-3.5 mr-2" /> Record Statutory Demand service…
              </DropdownMenuItem>
            </>
          )}
          {side.length > 0 && <DropdownMenuSeparator />}
          {side.map((m) => {
            const I = ICON[m.to] || Ban;
            return (
              <DropdownMenuItem key={m.to} onClick={() => pick(m)} className="text-slate-600" data-testid={`route-move-${m.to}`}>
                <I className="w-3.5 h-3.5 mr-2" /> {m.label}<span className="ml-auto text-[9px] text-slate-400">reason</span>
              </DropdownMenuItem>
            );
          })}
          {route.history?.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1 text-[10px] text-slate-500 flex items-center gap-1" data-testid="route-history"><History className="w-3 h-3" /> {route.history.length} move{route.history.length === 1 ? "" : "s"} · last by {route.history[route.history.length - 1].by} {new Date(route.history[route.history.length - 1].at).toLocaleDateString("en-GB")}</div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) { setPending(null); setReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="route-reason-dialog">
          <DialogHeader><DialogTitle className="text-sm">{pending?.label} — why?</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Logged on the file's timeline and in the route history. 5+ characters.</p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="text-sm" placeholder={pending?.to === "route_paused" ? "e.g. Breathing Space in force until 12/11" : pending?.to === "not_appropriate" ? "e.g. Debt below £5,000 threshold / guarantor already bankrupt" : "e.g. Paid in full / arrangement agreed and performing"} data-testid="route-reason-input" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setPending(null); setReason(""); }} data-testid="route-reason-cancel">Cancel</Button>
            <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={() => doMove(pending.to, reason)} disabled={busy || reason.trim().length < 5} data-testid="route-reason-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {gateFor && (
        <GateChecklistDialog caseRow={caseRow} gate={gateFor.gate} move={{ to: gateFor.to, label: gateFor.label }}
          onClose={() => setGateFor(null)} onMoved={(res) => applied(res, gateFor.to)} />
      )}
      {service && (
        <ServiceRecordDialog caseRow={caseRow} methods={service} onClose={() => setService(null)}
          onSaved={async (r) => { setService(null); if (r.route) setRoute(r.route); await onChanged?.(); }} />
      )}
      {review && (
        <ReviewOutcomeDialog caseRow={caseRow} onClose={() => setReview(false)}
          onDone={async (r) => { setReview(false); if (r?.route) setRoute(r.route); else await load(); await onChanged?.(); }} />
      )}
      {petition && (
        <PetitionHubDialog caseRow={caseRow} onClose={() => setPetition(false)}
          onRouteChanged={async (r) => { if (r) setRoute(r); else await load(); await onChanged?.(); }} />
      )}
    </>
  );
}
